// UnitDatesSteps.js
//
// Two steps of the unit view about dates (Spec 2 sections 3.5, 4.2, 4.8 and 6.4):
//
//   Dates and numbers (Types)   how this table's text dates such as 03/04/2026 are read.
//                               The order is a DateOrder decision on the FILE, keyed by the
//                               unit's id: None inherits the file's order, a person may set
//                               one, and a table whose own dates prove an order cannot be
//                               switched. Being a file decision, changing it is a file edit,
//                               which the dialog runs (and confirms when it would un-approve
//                               the file).
//   Reporting period (UnitDate) the date this table reports for, which orders the files
//                               checked against the table. Decode works it out; a person
//                               may set it, and Undo returns to Decode's. The date being
//                               typed is a draft until Apply (UnitStepFrame's useStepDraft).

import React, { useState } from 'react';
import { Box, Button, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import UnitStepFrame, { RESERVED_COLUMNS, SetAsideNotice, useStepDraft } from './UnitStepFrame';
import AppDatePicker from '../../common/AppDatePicker';
import { activeValue, activeVersion } from '../../../utils/decisions';
import {
  editFileDecisions,
  editUnitDecisions,
  resetFileDecision,
  resetUnitDecision,
} from '../../../services/cleaningGestures';
import { DAY_FIRST, MONTH_FIRST, describeDateReading, firstAmbiguousDate } from '../../../utils/dateOrder';

const DATE_ORDER = 'date_order';
const UNIT_DATE = 'unit_date';

const ORDER_LABELS = {
  [DAY_FIRST]: 'Day / Month / Year',
  [MONTH_FIRST]: 'Month / Day / Year',
};

const MODE_WORDS = { latest: 'latest', earliest: 'earliest', most_common: 'most common' };

// Props: number, companyId, tableId, file, unit, verdicts, fileBlock (why the file's own
// decisions cannot be edited now, '' when they can), onFileChange (the dialog's runner of a
// file edit, answering whether it was made).
export function DatesAndNumbersStep({
  number,
  companyId,
  tableId,
  file,
  unit,
  verdicts,
  fileBlock,
  onFileChange,
}) {
  const [busy, setBusy] = useState(false);
  const dateOrderRecord = file.processes?.[DATE_ORDER] || null;
  const own = dateOrderRecord ? activeVersion(dateOrderRecord, unit.id) : null;
  const fileOrder = dateOrderRecord ? activeValue(dateOrderRecord, 'file') : null;
  // The order the unit's own dates prove, from the evidence measured when its base was cut
  // (common/column_types.py DateOrderEvidence.proves): only one reading allowed by some of
  // its values (13/04/2026 is day first), and none allowing only the other.
  const dayFirst = unit.date_order_evidence?.day_first || 0;
  const monthFirst = unit.date_order_evidence?.month_first || 0;
  let proof = null;
  if (dayFirst && !monthFirst) proof = DAY_FIRST;
  if (monthFirst && !dayFirst) proof = MONTH_FIRST;

  const sampleValues = (unit.sample_rows || []).map((row) => {
    const values = { ...row };
    RESERVED_COLUMNS.forEach((column) => { delete values[column]; });
    return values;
  });
  const example = firstAmbiguousDate(sampleValues);

  let caption = 'Same as the file';
  if (proof) {
    caption = proof === fileOrder || !fileOrder
      ? `This table's own dates show ${ORDER_LABELS[proof]}`
      : `This table's own dates show ${ORDER_LABELS[proof]}, unlike the rest of the file`;
  } else if (own?.made_by === 'person') {
    caption = 'Set for this table by you';
  } else if (unit.date_order_assumed) {
    caption = 'Same as the file. Assumed: no date in this file shows which.';
  }

  const run = async (send) => {
    setBusy(true);
    await onFileChange(send);
    setBusy(false);
  };

  return (
    <UnitStepFrame
      number={number}
      title="Dates and numbers"
      verdicts={verdicts}
      record={unit.processes?.types || null}
      defaultOpen={verdicts.length > 0}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Numbers and dates are converted; a value that does not convert is listed above.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="body2">Dates in this table:</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={unit.date_order}
          disabled={Boolean(fileBlock) || Boolean(proof) || busy}
          onChange={(event, order) => {
            if (!order || order === unit.date_order) return;
            run(() => editFileDecisions(
              companyId, tableId, file.id, DATE_ORDER, [{ key: unit.id, value: order, refersTo: null }],
            ));
          }}
        >
          <ToggleButton value={DAY_FIRST} sx={{ textTransform: 'none' }}>{ORDER_LABELS[DAY_FIRST]}</ToggleButton>
          <ToggleButton value={MONTH_FIRST} sx={{ textTransform: 'none' }}>{ORDER_LABELS[MONTH_FIRST]}</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">{caption}</Typography>
        {own?.made_by === 'person' && (
          <Button
            size="small"
            disabled={Boolean(fileBlock) || busy}
            onClick={() => run(() => resetFileDecision(companyId, tableId, file.id, DATE_ORDER, unit.id))}
            sx={{ textTransform: 'none' }}
          >
            Use automatic date format
          </Button>
        )}
      </Box>
      {example && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {describeDateReading(example, unit.date_order)}
        </Typography>
      )}
      {fileBlock && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {fileBlock}
        </Typography>
      )}
    </UnitStepFrame>
  );
}

// Props: number, companyId, tableId, unit, table, verdicts, editBlock, and the unit view's
// draftEpoch and runUnitChange(send, onApplied).
export function ReportingPeriodStep({
  number,
  companyId,
  tableId,
  unit,
  table,
  verdicts,
  editBlock,
  draftEpoch,
  runUnitChange,
}) {
  // The draft: undefined while nothing is typed, otherwise the date the person entered
  // ('' while incomplete).
  const { changes: draft, setAsideChanges, change, clear, restore } = useStepDraft(draftEpoch, undefined);
  const [busy, setBusy] = useState(false);
  const record = unit.processes?.[UNIT_DATE] || null;
  const version = record ? activeVersion(record, 'value') : null;
  const saved = version?.value || '';
  const shown = draft === undefined ? saved : draft;
  const canApply = draft !== undefined && draft !== '' && draft !== saved;
  const setAsideCount = setAsideChanges != null && setAsideChanges !== '' && setAsideChanges !== saved ? 1 : 0;
  const disabled = Boolean(editBlock) || busy || setAsideCount > 0;
  const tableName = String(table.name || table.id || '').replace(/_/g, ' ');

  // Where Decode's reporting period came from, as the record's computed_from says.
  let caption = '';
  if (version?.made_by === 'person') {
    caption = 'Set by you';
  } else if (record?.computed_from === 'periods') {
    caption = `The ${MODE_WORDS[table.reporting_date_mode] || 'latest'} reporting period in this table`;
  } else if (record?.computed_from === 'file_name_date') {
    caption = 'The date in the file name: this table has no reporting period column';
  } else if (record?.computed_from === 'upload_time') {
    caption = 'The upload date: this table has no reporting period column and the file name holds no date';
  }

  const run = async (send) => {
    setBusy(true);
    await runUnitChange(send, clear);
    setBusy(false);
  };

  return (
    <UnitStepFrame
      number={number}
      title="Reporting period"
      verdicts={verdicts}
      record={record}
      defaultOpen={verdicts.length > 0}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {`The date this table reports for. Files are checked against ${tableName} in this order.`}
      </Typography>
      <SetAsideNotice changeCount={setAsideCount} editBlock={editBlock} onRestore={restore} onDiscard={clear} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <AppDatePicker
          label="Reporting period"
          value={shown}
          disabled={disabled}
          onChange={(value) => change(() => value)}
        />
        {caption && <Typography variant="caption" color="text.secondary">{caption}</Typography>}
        {canApply && (
          <Button
            size="small"
            variant="contained"
            disabled={disabled}
            onClick={() => run(() => editUnitDecisions(
              companyId, tableId, unit.id, UNIT_DATE, [{ key: 'value', value: draft, refersTo: null }],
            ))}
            sx={{ textTransform: 'none' }}
          >
            Apply
          </Button>
        )}
        {version?.made_by === 'person' && (
          <Button
            size="small"
            disabled={disabled}
            onClick={() => run(() => resetUnitDecision(companyId, tableId, unit.id, UNIT_DATE, 'value'))}
            sx={{ textTransform: 'none' }}
          >
            Undo
          </Button>
        )}
      </Box>
    </UnitStepFrame>
  );
}
